import {
  TRACKING_META_EXTRA_KEYS,
  TTrackingMeta,
  TTrackingMetaColumns,
  TTrackingMetaExtras,
} from 'shared/domain/entities';
import IHTTPProvider from 'shared/domain/providers/IHTTPProvider';
import ICustomersRepository, {
  TCreateParams,
  TCustomerUpdateParams,
} from 'shared/domain/repositories/ICustomersRepository';
import { Customer, IVectisCustomer } from 'shared/infra/adapters';

type TVectisUpdateParams = {
  email?: string;
  cpf?: string;
  rg?: string;
  nome?: string;
  sobrenome?: string;
  foneCelular?: string;
  dataNascimento?: string;
  cep?: string;
  logradouro?: string;
  numeroLogradouro?: string;
  idCidade?: number;
  idBairro?: number;
  numeroDocumentoFiscal?: string;
  complemento?: string;
  responsavel?: {
    nome?: string;
    sobrenome?: string;
    dataNascimento?: string;
    foneCelular?: string;
    cpf?: string;
    rg?: string;
    cep?: string;
    logradouro?: string;
    numeroLogradouro?: string;
    idCidade?: number;
    idBairro?: number;
    numeroDocumentoFiscal?: string;
    complemento?: string;
  };
};

const DEFAULT_UPDATE_ORIGIN = 'SITE';

export default class VectisCustomersRepository implements ICustomersRepository {
  constructor(private httpProvider: IHTTPProvider) {}

  public create: ICustomersRepository['create'] = async ({
    name,
    email,
    countryCode,
    origin,
    phone,
    terms,
    password,
    unitId,
    recommenderId,
    recommenderUnitId,
    optin,
    catalogId,
    contactTypeId,
    leadOrigin,
    registerType,
    trackingMeta = {},
  }) => {
    const [firstName, ...lastNames] = name.split(' ');
    const { columns, extras } = this.splitTrackingMeta(trackingMeta);

    const customer = await this.httpProvider.put<IVectisCustomer>('/clientes', {
      nome: firstName,
      sobrenome: lastNames.join(' '),
      email,
      ddi: countryCode,
      foneCelular: phone,
      idUnidade: unitId,
      tipoPessoa: 'FISICA',
      origemCadastro: origin,
      statusEmail: 'VALIDO',
      statusWhatsapp: 'VALIDO',
      senhaCliente: password,
      idClienteIndicacao: recommenderId,
      idUnidadeIndicador: recommenderUnitId,
      termoPoliticaPrivacidade: terms,
      idTipoContato: contactTypeId,
      idCatalogoProcedencia: catalogId,
      optin,
      ...columns,
      ...this.generateExtraPayloadBaseOnRegisterType(registerType),
      origemLead: this.generateLeadOrigin(leadOrigin, extras),
    });

    return new Customer(customer);
  };

  public update: ICustomersRepository['update'] = async ({ id, ...params }) => {
    const body: TVectisUpdateParams = this.buildUpdateBody(params);
    const customer = await this.httpProvider.post<IVectisCustomer>(
      `clientes/${id}`,
      body,
    );

    return new Customer(customer);
  };

  public findById: ICustomersRepository['findById'] = async id => {
    const customer = await this.httpProvider.get<IVectisCustomer | null>(
      `/clientes/${id}`,
    );

    if (!customer) return null;

    return new Customer(customer);
  };

  public findByEmail: ICustomersRepository['findByEmail'] = async email => {
    const customer = await this.httpProvider.get<IVectisCustomer | null>(
      `/clientes`,
      {
        params: { email },
      },
    );

    if (!customer) return null;

    return new Customer(customer);
  };

  public findByDocument: ICustomersRepository['findByDocument'] =
    async document => {
      const customer = await this.httpProvider.get<IVectisCustomer | null>(
        `/clientes`,
        {
          params: { cpfCnpj: document },
        },
      );

      if (!customer) return null;
      return new Customer(customer);
    };

  private generateExtraPayloadBaseOnRegisterType(
    type?: TCreateParams['registerType'],
  ) {
    if (type === 'LANDING_PAGE_RECOMMENDATION') {
      return {
        indicacaoLandingPage: true,
      };
    }

    return {};
  }

  /**
   * O Vectis tem coluna dedicada em `hclinica.cliente` para parte do bloco de
   * atribuição — esses campos vão na raiz do payload. O restante não tem coluna
   * e é acomodado em `origemLead.extra`, que é um `jsonb` livre (ED-7201).
   */
  private splitTrackingMeta(trackingMeta: TTrackingMeta) {
    const extras: TTrackingMetaExtras = {};
    const columns: TTrackingMetaColumns = { ...trackingMeta };

    TRACKING_META_EXTRA_KEYS.forEach(key => {
      const value = trackingMeta[key];
      if (value) extras[key] = value;

      delete (columns as TTrackingMeta)[key];
    });

    return { columns, extras };
  }

  private generateLeadOrigin(
    leadOrigin: TCreateParams['leadOrigin'],
    trackingExtras: TTrackingMetaExtras = {},
  ) {
    const extra = { ...leadOrigin.meta, ...trackingExtras };

    return {
      identificador: leadOrigin.key,
      canal: leadOrigin.channel,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
  }

  private buildUpdateBody(params: Omit<TCustomerUpdateParams, 'id'>) {
    const origin = params.origin || DEFAULT_UPDATE_ORIGIN;

    const customerBody = {
      nome: params.firstName,
      sobrenome: params.lastName,
      cep: params.address?.postalCode,
      idCidade: params.address?.city?.id,
      idBairro: params.address?.district?.id,
      logradouro: params.address?.street,
      numeroLogradouro: params.address?.number,
      complemento: params.address?.additionalInformation,
      email: params.email,
      foneCelular: params.phone,
      dataNascimento: params.birthdate,
      cpf: params.document?.replaceAll('_', ''),
      rg: params.rgDocument ?? params.alternativeDocument,
      numeroDocumentoFiscal: params.taxDocument ?? params.alternativeDocument,
      origemAtualizacao: origin,
    };

    if (params.minorityStatus === 'ADULT') return customerBody;

    return {
      ...customerBody,
      responsavel: params.guardian
        ? {
            nome: params.guardian.firstName,
            sobrenome: params.guardian.lastName,
            cep: params.guardian.address?.postalCode,
            idCidade: params.guardian.address?.city?.id,
            idBairro: params.guardian.address?.district?.id,
            logradouro: params.guardian.address?.street,
            numeroLogradouro: params.guardian.address?.number,
            complemento: params.guardian.address?.additionalInformation,
            cpf: params.guardian.document?.replaceAll('_', ''),
            rg:
              params.guardian.rgDocument ?? params.guardian.alternativeDocument,
            numeroDocumentoFiscal:
              params.guardian.taxDocument ??
              params.guardian.alternativeDocument,
            dataNascimento: params.guardian.birthdate,
            email: params.guardian.email,
            foneCelular: params.guardian.phone,
            origemCadastro: origin,
          }
        : undefined,
    };
  }
}
